// Hardened rehype-sanitize schema + URL scheme guard for the Markdown render
// pipeline (RFC-0010 §2, PD-0010 render half — body links).
//
// Two layers, both applied to the Markdown-derived HAST tree:
//
//   1. `rehypeGuardUrls` — a small in-repo rehype plugin that strips any
//      URL-bearing attribute (`href`/`src`/`cite`/…) whose value is not an
//      absolute `http(s)` URL or a relative reference (see `isSafeHttpUrl`).
//      This is what closes the scheme-relative `//host` bypass that a plain
//      protocol allowlist misses.
//
//   2. `sanitizeSchema` — rehype-sanitize's `defaultSchema` narrowed so every
//      URL attribute is restricted to `http`/`https` and widened just enough
//      to let the trusted, post-sanitize transforms (rehype-katex math spans)
//      survive.
//
// Ordering note: rehype-sanitize runs BEFORE rehype-slug / -autolink-headings /
// -highlight / -katex. Those plugins generate markup from already-sanitized
// text with our own trusted code, so running them after the sanitizer is the
// documented-safe pattern (rehype-katex README) and avoids having to allow
// KaTeX's large generated subtree through the schema.

import { defaultSchema } from 'rehype-sanitize';
import { isSafeHttpUrl } from './url-scheme';

// HAST property keys (compared case-insensitively) that carry a URL and must be
// scheme-guarded. camelCase HAST names (`longDesc`, `formAction`, `xlinkHref`)
// lowercase to these entries.
const URL_ATTRIBUTE_KEYS = new Set([
  'href',
  'src',
  'cite',
  'longdesc',
  'poster',
  'background',
  'action',
  'formaction',
  'data',
  'xlinkhref',
  'srcset',
]);

interface HastNode {
  type: string;
  tagName?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
}

function guardNode(node: HastNode): void {
  if (node.type === 'element' && node.properties) {
    for (const key of Object.keys(node.properties)) {
      if (!URL_ATTRIBUTE_KEYS.has(key.toLowerCase())) continue;
      const value = node.properties[key];
      if (typeof value === 'string' && !isSafeHttpUrl(value)) {
        // Drop the attribute entirely — no clickable/loadable dangerous URL.
        delete node.properties[key];
      }
    }
  }
  if (node.children) {
    for (const child of node.children) guardNode(child);
  }
}

/**
 * rehype plugin: recursively removes URL-bearing attributes that fail the
 * `http(s)`-or-relative scheme guard. Runs alongside rehype-sanitize as the
 * scheme-relative-bypass–closing layer of the §2 protocol allowlist.
 */
export function rehypeGuardUrls() {
  return (tree: HastNode) => {
    guardNode(tree);
    return tree;
  };
}

type SanitizeSchema = typeof defaultSchema;

/**
 * rehype-sanitize schema: the default (which already strips `<script>`, event
 * handlers, `<style>`, etc.), with every URL protocol pinned to `http`/`https`
 * and the pre-KaTeX math class names allowed through so `rehype-katex` (which
 * runs after the sanitizer) can find and render them.
 */
export const sanitizeSchema: SanitizeSchema = {
  ...defaultSchema,
  // Restrict every URL attribute to http/https only — no mailto/tel/etc. and,
  // combined with `rehypeGuardUrls`, no javascript:/data:/vbscript:// bypass.
  protocols: {
    ...defaultSchema.protocols,
    href: ['http', 'https'],
    src: ['http', 'https'],
    cite: ['http', 'https'],
    longDesc: ['http', 'https'],
  },
  attributes: {
    ...defaultSchema.attributes,
    // remark-math + remark-rehype emit these class names on the container that
    // rehype-katex later transforms; keep them so math survives sanitization.
    div: [
      ...(defaultSchema.attributes?.div ?? []),
      ['className', 'math', 'math-display'],
    ],
    span: [
      ...(defaultSchema.attributes?.span ?? []),
      ['className', 'math', 'math-inline'],
    ],
  },
};
